// PKB API Service для работы с ГБД "Юридические лица"
// Техническое задание: Клиент для API ГБД "Юридические лица"

import {
    getCurrentEnvironment,
    getPKBUrl,
    getPKBHeaders,
    getBasicAuthHeaders,
    PKB_ENDPOINTS,
    getErrorMessage,
    validateCredentials,
    DEFAULT_PKB_CONFIG,
    LOG_CONFIG,
    PKB_AUTH_ERROR_CODES,
    PKB_AUTH_ERROR_MESSAGES
} from '../config/pkbConfig';

interface PKBTokenResponse {
    access: {
        hash: string;
        expires_at: string;
        ttl: number;
        issue_date: string;
    };
    refresh: {
        hash: string;
        expires_at: string;
        ttl: number;
        issue_date: string;
    };
    pass_change_needed: boolean;
}

interface PKBOrganization {
    bin: string;
    name_ru: string;
    name_kk?: string;
    name_en?: string;
    reg_status: {
        code: string;
        ru: string;
        kk?: string;
    };
    organization_leader?: {
        iin: string;
        position?: string;
        appointment_date?: string;
        position_type?: string;
    };
    founders_ul?: Array<{ bin: string }>;
    founders_fl?: Array<{ iin: string }>;
    address: {
        district_ru?: string;
        region_ru?: string;
        city_ru?: string;
        street_ru?: string;
        building_number?: string;
        apartment_number?: string;
        postal_code?: string;
        classifier_codes?: {
            region_code?: string;
            district_code?: string;
        };
        premises_type?: string;
    };
    activity: {
        main: boolean;
        oked: string;
        activity_ru: string;
        activity_kk?: string;
    }[];
    registration_date?: string;
    last_change_date?: string;
    legal_form?: {
        code: string;
        ru: string;
        kk?: string;
    };
    entrepreneurship_type?: {
        code: string;
        ru: string;
        kk?: string;
    };
    beneficiaries?: Array<{
        iin?: string;
        bin?: string;
        share_percentage?: number;
    }>;
}

interface PKBCompanyResponse {
    organization: PKBOrganization;
}

interface PKBErrorResponse {
    error: string;
    message: string;
    code?: string;
}

class PKBApiService {
    private baseUrl: string;
    private accessToken: string | null = null;
    private refreshToken: string | null = null;
    private tokenExpiry: number | null = null;
    private refreshTokenExpiry: number | null = null;
    private credentials: { login: string; password: string } | null = null;

    constructor() {
        // Получаем текущую среду из конфигурации
        const environment = getCurrentEnvironment();
        this.baseUrl = environment.baseUrl;

        // Загружаем сохраненные токены
        this.loadTokensFromStorage();

        if (LOG_CONFIG.enableConsoleLog) {
            console.log(`PKB API Service initialized with environment: ${environment.name} (${this.baseUrl})`);
        }
    }

    /**
     * Установка учетных данных для авторизации
     */
    setCredentials(login: string, password: string): void {
        const validation = validateCredentials(login, password);
        if (!validation.isValid) {
            throw new Error(validation.error);
        }

        this.credentials = { login, password };
        // Сохраняем в localStorage для удобства разработки
        localStorage.setItem('pkb_credentials', JSON.stringify({ login, password }));

        if (LOG_CONFIG.enableConsoleLog) {
            console.log('PKB credentials set successfully');
        }
    }

    /**
     * Загрузка токенов из localStorage
     */
    private loadTokensFromStorage(): void {
        try {
            const tokens = localStorage.getItem('pkb_tokens');
            if (tokens) {
                const parsedTokens = JSON.parse(tokens);
                this.accessToken = parsedTokens.accessToken;
                this.refreshToken = parsedTokens.refreshToken;
                this.tokenExpiry = parsedTokens.tokenExpiry;
                this.refreshTokenExpiry = parsedTokens.refreshTokenExpiry;
            }

            const credentials = localStorage.getItem('pkb_credentials');
            if (credentials) {
                this.credentials = JSON.parse(credentials);
            }
        } catch (error) {
            console.warn('Ошибка загрузки токенов ПКБ:', error);
        }
    }

    /**
     * Сохранение токенов в localStorage
     */
    private saveTokensToStorage(): void {
        try {
            const tokens = {
                accessToken: this.accessToken,
                refreshToken: this.refreshToken,
                tokenExpiry: this.tokenExpiry,
                refreshTokenExpiry: this.refreshTokenExpiry
            };
            localStorage.setItem('pkb_tokens', JSON.stringify(tokens));
        } catch (error) {
            console.warn('Ошибка сохранения токенов ПКБ:', error);
        }
    }

    /**
     * Проверка валидности токена
     */
    private isTokenValid(): boolean {
        if (!this.accessToken || !this.tokenExpiry) {
            return false;
        }
        return Date.now() < this.tokenExpiry - DEFAULT_PKB_CONFIG.tokenRefreshBuffer;
    }

    /**
     * Проверка валидности refresh токена
     */
    private isRefreshTokenValid(): boolean {
        if (!this.refreshToken || !this.refreshTokenExpiry) {
            return false;
        }
        return Date.now() < this.refreshTokenExpiry - DEFAULT_PKB_CONFIG.tokenRefreshBuffer;
    }

    /**
     * Получение токена авторизации
     */
    private async authenticate(): Promise<void> {
        if (!this.credentials) {
            throw new Error('Учетные данные ПКБ не настроены');
        }

        // Создаем Basic авторизацию
        const authString = btoa(`${this.credentials.login}:${this.credentials.password}`);
        const url = getPKBUrl(PKB_ENDPOINTS.LOGIN);

        if (LOG_CONFIG.enableNetworkLog) {
            console.log('PKB: Authenticating...', { url, login: this.credentials.login });
        }

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Basic ${authString}`,
                'X-Client-Type': 'API',
                'Content-Type': 'application/json'
            },
            signal: AbortSignal.timeout(DEFAULT_PKB_CONFIG.timeout)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));

            // Обработка специфичных ошибок ПКБ
            if (response.status === 401) {
                if (errorData.code === PKB_AUTH_ERROR_CODES.WRONG_CREDENTIALS) {
                    throw new Error(PKB_AUTH_ERROR_MESSAGES[PKB_AUTH_ERROR_CODES.WRONG_CREDENTIALS]);
                } else if (errorData.code === PKB_AUTH_ERROR_CODES.USER_NOT_FOUND) {
                    throw new Error(PKB_AUTH_ERROR_MESSAGES[PKB_AUTH_ERROR_CODES.USER_NOT_FOUND]);
                } else if (errorData.code === PKB_AUTH_ERROR_CODES.USER_BLOCKED) {
                    throw new Error(PKB_AUTH_ERROR_MESSAGES[PKB_AUTH_ERROR_CODES.USER_BLOCKED]);
                } else if (errorData.code === PKB_AUTH_ERROR_CODES.SYSTEM_ERROR) {
                    throw new Error(PKB_AUTH_ERROR_MESSAGES[PKB_AUTH_ERROR_CODES.SYSTEM_ERROR]);
                } else {
                    throw new Error(`Ошибка авторизации: ${errorData.message || 'Неверные учетные данные'}`);
                }
            }

            const errorMessage = getErrorMessage(response.status);
            throw new Error(`${errorMessage} (${response.status} ${response.statusText}). ${errorData.message || ''}`);
        }

        const tokenData: PKBTokenResponse = await response.json();

        // Проверяем, нужно ли изменить пароль
        if (tokenData.pass_change_needed) {
            throw new Error('Требуется смена пароля. Обратитесь к администратору ПКБ.');
        }

        this.accessToken = tokenData.access.hash;
        this.refreshToken = tokenData.refresh.hash;
        this.tokenExpiry = new Date(tokenData.access.expires_at).getTime();
        this.refreshTokenExpiry = new Date(tokenData.refresh.expires_at).getTime();

        this.saveTokensToStorage();

        if (LOG_CONFIG.enableConsoleLog) {
            console.log('PKB: Authentication successful', {
                accessExpires: tokenData.access.expires_at,
                refreshExpires: tokenData.refresh.expires_at,
                accessTTL: tokenData.access.ttl,
                refreshTTL: tokenData.refresh.ttl
            });
        }
    }

    /**
     * Обновление токена
     */
    private async refreshAccessToken(): Promise<void> {
        if (!this.refreshToken) {
            throw new Error('Refresh токен отсутствует');
        }

        const url = getPKBUrl(PKB_ENDPOINTS.REFRESH);

        if (LOG_CONFIG.enableNetworkLog) {
            console.log('PKB: Refreshing token...', { url });
        }

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${this.refreshToken}`,
                'X-Client-Type': 'API',
                'Content-Type': 'application/json'
            },
            signal: AbortSignal.timeout(DEFAULT_PKB_CONFIG.timeout)
        });

        if (!response.ok) {
            // Если refresh не удался, пробуем заново авторизоваться
            if (LOG_CONFIG.enableConsoleLog) {
                console.log('PKB: Token refresh failed, re-authenticating...');
            }
            this.accessToken = null;
            this.refreshToken = null;
            this.tokenExpiry = null;
            this.refreshTokenExpiry = null;
            await this.authenticate();
            return;
        }

        const tokenData: PKBTokenResponse = await response.json();

        // Проверяем, нужно ли изменить пароль
        if (tokenData.pass_change_needed) {
            throw new Error('Требуется смена пароля. Обратитесь к администратору ПКБ.');
        }

        this.accessToken = tokenData.access.hash;
        this.refreshToken = tokenData.refresh.hash;
        this.tokenExpiry = new Date(tokenData.access.expires_at).getTime();
        this.refreshTokenExpiry = new Date(tokenData.refresh.expires_at).getTime();

        this.saveTokensToStorage();

        if (LOG_CONFIG.enableConsoleLog) {
            console.log('PKB: Token refreshed successfully', {
                accessExpires: tokenData.access.expires_at,
                refreshExpires: tokenData.refresh.expires_at
            });
        }
    }

    /**
     * Получение валидного токена (с автоматическим обновлением)
     */
    private async getValidToken(): Promise<string> {
        if (this.isTokenValid()) {
            return this.accessToken!;
        }

        if (this.isRefreshTokenValid()) {
            await this.refreshAccessToken();
            return this.accessToken!;
        }

        await this.authenticate();
        return this.accessToken!;
    }

    /**
     * Поиск компании по БИН
     */
    async searchCompanyByBin(bin: string): Promise<PKBOrganization> {
        // Валидация БИН
        if (!/^\d{12}$/.test(bin)) {
            throw new Error('БИН должен содержать 12 цифр');
        }

        // Используем наш backend API вместо прямого обращения к ПКБ
        const backendUrl = `/api/pkb/company/${bin}`;

        if (LOG_CONFIG.enableNetworkLog) {
            console.log('PKB: Searching company via backend API...', { backendUrl, bin });
        }

        const response = await fetch(backendUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            signal: AbortSignal.timeout(DEFAULT_PKB_CONFIG.timeout)
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('Компания с указанным БИН не найдена в базе ПКБ');
            }

            const errorData: any = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Ошибка при запросе к ПКБ (${response.status})`);
        }

        const data = await response.json();

        // Проверяем, что получили данные от нашего backend (флат структура)
        // Проверяем наличие ключевых полей нашего backend формата
        if (data.found === true && (data.companyName !== undefined || data.bin !== undefined)) {
            // Преобразуем ответ backend в формат PKBOrganization
            const pkbOrganization: PKBOrganization = {
                bin: data.bin || bin,
                name_ru: data.companyName || '',
                name_kk: data.companyNameKz,
                name_en: data.companyNameEn,
                reg_status: {
                    code: data.statusCode || '1',
                    ru: data.status || 'Действующее',
                    kk: data.status
                },
                organization_leader: {
                    iin: data.directorIin || '',
                    position: data.directorPosition
                },
                address: {
                    district_ru: data.region,
                    region_ru: data.region,
                    city_ru: data.city,
                    street_ru: data.street,
                    building_number: data.building,
                    postal_code: data.postalCode
                },
                activity: [],
                registration_date: data.registrationDate,
                last_change_date: data.lastChangeDate,
                legal_form: data.legalForm ? {
                    code: 'TOO',
                    ru: data.legalForm,
                    kk: data.legalForm
                } : undefined,
                founders_ul: [],
                founders_fl: []
            };

            if (LOG_CONFIG.enableConsoleLog) {
                console.log('PKB: Company found successfully', { bin: data.bin, name: data.companyName });
            }

            return pkbOrganization;
        }

        // Проверяем, если компания не найдена в базе
        if (data.found === false) {
            throw new Error(data.message || 'Компания с указанным БИН не найдена в базе ПКБ');
        }

        // Если получили структуру с organization (старый формат)
        if (data.organization) {
            if (LOG_CONFIG.enableConsoleLog) {
                console.log('PKB: Company found successfully', { bin, name: data.organization.name_ru });
            }
            return data.organization;
        }

        // Если структура не распознана, возвращаем как есть
        throw new Error('Неизвестный формат ответа от PKB API');
    }

    /**
     * Генерация mock данных для компании по БИН
     */
    private getMockCompanyData(bin: string): PKBOrganization {
        const companies = {
            '222222222222': {
                name_ru: 'ТОО "Алматинская птицефабрика"',
                name_kk: 'Алматы құс фабрикасы ЖШС',
                address: {
                    district_ru: 'Алматы',
                    region_ru: 'Алматинская область',
                    city_ru: 'Алматы',
                    street_ru: 'ул. Промышленная',
                    building_number: '15'
                },
                organization_leader: {
                    iin: '123456789012',
                    position: 'Директор'
                }
            },
            '111111111111': {
                name_ru: 'ИП "Сейтова А.А."',
                name_kk: 'ЖШ "Сейтова А.А."',
                address: {
                    district_ru: 'Шымкент',
                    region_ru: 'Туркестанская область',
                    city_ru: 'Шымкент',
                    street_ru: 'ул. Абая',
                    building_number: '45'
                },
                organization_leader: {
                    iin: '987654321098',
                    position: 'Индивидуальный предприниматель'
                }
            },
            '333333333333': {
                name_ru: 'ТОО "Астана Агро"',
                name_kk: 'Астана Агро ЖШС',
                address: {
                    district_ru: 'Нур-Султан',
                    region_ru: 'Акмолинская область',
                    city_ru: 'Нур-Султан',
                    street_ru: 'пр. Кабанбай батыра',
                    building_number: '32'
                },
                organization_leader: {
                    iin: '456789123456',
                    position: 'Генеральный директор'
                }
            }
        };

        const companyData = companies[bin as keyof typeof companies] || {
            name_ru: `ТОО "Тестовая компания ${bin.slice(-4)}"`,
            name_kk: `Тестілік компания ${bin.slice(-4)} ЖШС`,
            address: {
                district_ru: 'Алматы',
                region_ru: 'Алматинская область',
                city_ru: 'Алматы',
                street_ru: 'ул. Тестовая',
                building_number: '1'
            },
            organization_leader: {
                iin: bin.slice(0, 12),
                position: 'Директор'
            }
        };

        return {
            bin: bin,
            name_ru: companyData.name_ru,
            name_kk: companyData.name_kk,
            reg_status: {
                code: 'ACTIVE',
                ru: 'Действующее',
                kk: 'Белсенді'
            },
            organization_leader: companyData.organization_leader,
            address: companyData.address,
            founders_ul: [],
            founders_fl: [],
            activity: [{
                main: true,
                oked: '01.11',
                activity_ru: 'Выращивание сельскохозяйственных культур',
                activity_kk: 'Ауылшаруашылық дақылдарын өсіру'
            }],
            registration_date: '2020-01-15',
            last_change_date: '2024-01-01',
            legal_form: {
                code: 'TOO',
                ru: 'Товарищество с ограниченной ответственностью',
                kk: 'Жауапкершілігі шектеулі серіктестік'
            },
            entrepreneurship_type: {
                code: 'LEGAL',
                ru: 'Юридическое лицо',
                kk: 'Заңды тұлға'
            },
            beneficiaries: []
        };
    }

    /**
     * Преобразование данных ПКБ в формат приложения
     */
    transformPKBDataToCompany(pkbData: any): any {
        // Проверяем, если это уже преобразованные данные от нашего backend
        if (pkbData.companyName && !pkbData.name_ru) {
            // Данные уже в нужном формате от backend
            return {
                bin: pkbData.bin,
                name: pkbData.companyName || `Компания ${pkbData.bin}`,
                legalAddress: pkbData.legalAddress || 'Адрес не указан',
                actualAddress: pkbData.actualAddress || pkbData.legalAddress || 'Адрес не указан',
                contactPhone: this.generateMockPhone(pkbData.bin),
                contactEmail: this.generateMockEmail(pkbData.bin),
                directorName: pkbData.directorName || 'Информация о директоре недоступна',
                bank: this.generateMockBank(pkbData.bin),
                kbe: this.generateMockKbe(pkbData.bin),
                bik: this.generateMockBik(pkbData.bin),
                accountNumber: this.generateMockAccountNumber(pkbData.bin),
                pkbData: {
                    status: pkbData.status ? { ru: pkbData.status, code: pkbData.statusCode } : undefined,
                    registrationDate: pkbData.registrationDate,
                    lastChangeDate: pkbData.lastChangeDate,
                    legalForm: pkbData.legalForm,
                    founders: {
                        legal: [],
                        individual: []
                    },
                    activities: [],
                    beneficiaries: []
                }
            };
        }

        // Обработка стандартного формата PKBOrganization
        // Формируем полный адрес
        const addressParts = [
            pkbData.address?.district_ru,
            pkbData.address?.region_ru,
            pkbData.address?.city_ru,
            pkbData.address?.street_ru,
            pkbData.address?.building_number
        ].filter(Boolean);

        const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : 'Адрес не указан';

        // Формируем имя директора
        const directorName = pkbData.organization_leader?.iin
            ? `${pkbData.organization_leader.position || 'Директор'} (ИИН: ${pkbData.organization_leader.iin})`
            : 'Информация о директоре недоступна';

        // Генерируем тестовые контакты для mock данных
        const contactPhone = this.generateMockPhone(pkbData.bin);
        const contactEmail = this.generateMockEmail(pkbData.bin);

        return {
            bin: pkbData.bin,
            name: pkbData.name_ru || `Компания ${pkbData.bin}`,
            legalAddress: fullAddress,
            actualAddress: fullAddress, // Используем тот же адрес
            contactPhone: contactPhone,
            contactEmail: contactEmail,
            directorName: directorName,
            bank: this.generateMockBank(pkbData.bin),
            kbe: this.generateMockKbe(pkbData.bin),
            bik: this.generateMockBik(pkbData.bin),
            accountNumber: this.generateMockAccountNumber(pkbData.bin),
            pkbData: {
                status: pkbData.reg_status,
                registrationDate: pkbData.registration_date,
                lastChangeDate: pkbData.last_change_date,
                legalForm: pkbData.legal_form,
                entrepreneurshipType: pkbData.entrepreneurship_type,
                founders: {
                    legal: pkbData.founders_ul || [],
                    individual: pkbData.founders_fl || []
                },
                activities: pkbData.activity || [],
                beneficiaries: pkbData.beneficiaries || []
            }
        };
    }

    /**
     * Генерация mock телефона на основе БИН
     */
    private generateMockPhone(bin: string): string {
        const phoneNumbers = {
            '222222222222': '+77272345678',
            '111111111111': '+77252987654',
            '333333333333': '+77172456789'
        };
        return phoneNumbers[bin as keyof typeof phoneNumbers] || `+7777${bin.slice(-8)}`;
    }

    /**
     * Генерация mock email на основе БИН
     */
    private generateMockEmail(bin: string): string {
        const emails = {
            '222222222222': 'info@almaty-poultry.kz',
            '111111111111': 'seitova@gmail.com',
            '333333333333': 'contact@astana-agro.kz'
        };
        return emails[bin as keyof typeof emails] || `info@company${bin.slice(-4)}.kz`;
    }

    /**
     * Генерация mock банка на основе БИН
     */
    private generateMockBank(bin: string): string {
        const banks = {
            '222222222222': 'АО "Народный Банк Казахстана"',
            '111111111111': 'АО "Казкоммерцбанк"',
            '333333333333': 'АО "Банк ЦентрКредит"'
        };
        return banks[bin as keyof typeof banks] || `Банк ${bin.slice(-4)}`;
    }

    /**
     * Генерация mock КБе на основе БИН
     */
    private generateMockKbe(bin: string): string {
        const kbes = {
            '222222222222': '18',
            '111111111111': '17',
            '333333333333': '19'
        };
        return kbes[bin as keyof typeof kbes] || '18';
    }

    /**
     * Генерация mock БИК на основе БИН
     */
    private generateMockBik(bin: string): string {
        const biks = {
            '222222222222': '190101001',
            '111111111111': '190101002',
            '333333333333': '190101003'
        };
        return biks[bin as keyof typeof biks] || `190${bin.slice(-6)}`;
    }

    /**
     * Генерация mock номера счета на основе БИН
     */
    private generateMockAccountNumber(bin: string): string {
        const accounts = {
            '222222222222': 'KZ12345678901234567890',
            '111111111111': 'KZ12345678901234567891',
            '333333333333': 'KZ12345678901234567892'
        };
        return accounts[bin as keyof typeof accounts] || `KZ1234567890123456${bin.slice(-4)}`;
    }

    /**
     * Проверка статуса подключения к ПКБ
     */
    async checkConnection(): Promise<boolean> {
        try {
            await this.getValidToken();
            return true;
        } catch (error) {
            console.error('Ошибка подключения к ПКБ:', error);
            return false;
        }
    }

    /**
     * Очистка токенов
     */
    clearTokens(): void {
        this.accessToken = null;
        this.refreshToken = null;
        this.tokenExpiry = null;
        this.refreshTokenExpiry = null;
        localStorage.removeItem('pkb_tokens');
    }
}

// Экспортируем singleton instance
export const pkbApiService = new PKBApiService();

// Экспортируем типы
export type { PKBOrganization, PKBCompanyResponse, PKBErrorResponse };
